import { Skeleton, Stack } from "@mui/material";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { gql } from "generated-graphql";
import { FacilityVendorsQuery, FacilityVendorsQueryVariables } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { Suspense, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useFacilityWasteLineItemsDefaultSearch } from "../useFacilityWasteLineItemsDefaultSearch";
import { useWasteFacility } from "../useWasteFacility";
import { useWasteFacilityState } from "../WasteFacilityContext";
const VENDOR_TYPE_LABELS: Record<string, string> = {
  TRANSPORTER: "Transporter",
  TSDF: "TSDF",
};

const FACILITY_VENDORS_QUERY = gql(`
  query FacilityVendors(
    $facilityId: ID!
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
    $startDate: Date
    $endDate: Date
    $view: FacilityWasteView
    $tags: [String!]
  ) {
    facility(id: $facilityId) {
      facilityVendors(
        search: $search
        page: $page
        pageSize: $pageSize
        sort: $sort
        startDate: $startDate
        endDate: $endDate
        view: $view
        tags: $tags
      ) {
        items {
          id
          siteId
          siteName
          address
          city
          state
          zip
          vendorType
        }
        count
      }
    }
  }
`);

type VendorRow = NonNullable<
  NonNullable<NonNullable<FacilityVendorsQuery["facility"]>["facilityVendors"]>["items"]
>[number];

interface VendorsDataGridProps {
  facilityId: string;
}

const VendorsDataGridInner = ({ facilityId }: VendorsDataGridProps) => {
  const { tenantId } = useTenant();
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { facilityId: urlFacilityId } = useParams<{ facilityId: string }>();
  const { data: facilityData } = useWasteFacility(urlFacilityId);

  const defaultSearch = useFacilityWasteLineItemsDefaultSearch();

  const exportFilename = useMemo(
    () => (facilityData.facility?.name ? `${facilityData.facility.name}-vendors` : undefined),
    [facilityData.facility?.name]
  );

  const columns: OmnisearchGridColDef<VendorRow>[] = useMemo(
    () => [
      {
        field: "siteId",
        headerName: "Site ID / EPA ID",
        flex: 0.5,
        minWidth: 130,
      },
      {
        field: "siteName",
        headerName: "Site Name",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "address",
        headerName: "Address",
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: "city",
        headerName: "City",
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: "state",
        headerName: "State/Territory",
        flex: 0.3,
        minWidth: 70,
      },
      {
        field: "zip",
        headerName: "Zip",
        flex: 0.3,
        minWidth: 80,
      },
      {
        field: "vendorType",
        headerName: "Type",
        flex: 0.4,
        minWidth: 110,
        filterable: false,
        renderCell: ({ value }) => VENDOR_TYPE_LABELS[value] ?? value,
      },
    ],
    []
  );

  return (
    <OmnisearchDataGrid<FacilityVendorsQuery, FacilityVendorsQueryVariables, VendorRow>
      persistenceKey="facility-vendors-table"
      isURLDriven={false}
      columns={columns}
      dataQuery={FACILITY_VENDORS_QUERY}
      dataQueryVariables={{
        facilityId,
        startDate: deferredFilters.startDate,
        endDate: deferredFilters.endDate,
        view: deferredFilters.view,
        tags: deferredFilters.tags,
      }}
      getItems={(data) => data.facility?.facilityVendors?.items ?? []}
      getCount={(data) => data.facility?.facilityVendors?.count ?? 0}
      initialPageSize={10}
      withPadding={false}
      defaultSearch={defaultSearch}
      noDataMessage="No vendors found for this facility in the selected date range."
      initialSortModel={[{ field: "siteName", sort: "asc" }]}
      isRowSelectable={() => false}
      disableRowSelectionOnClick
      additionalFilterColumns={[
        {
          header: "Type",
          key: "vendorType",
          filterKeyType: "enumAutocomplete",
          enumValues: ["TRANSPORTER", "TSDF"],
          enumPresentationFunction: (value: string) => VENDOR_TYPE_LABELS[value] ?? value,
        },
      ]}
      commandButtons={[
        <ExportDataButton
          key="export"
          iconOnly
          exportType="facilityVendors"
          disabled={!facilityId}
          facilityId={facilityId}
          tenantId={tenantId ?? ""}
          fileName={exportFilename}
          filter={{
            startDate: deferredFilters.startDate,
            endDate: deferredFilters.endDate,
            view: deferredFilters.view,
            tags: deferredFilters.tags,
          }}
        />,
      ]}
    />
  );
};

export const VendorsDataGrid = ({ facilityId }: VendorsDataGridProps) => {
  return (
    <Suspense
      fallback={
        <Stack spacing={2} width="100%">
          <Skeleton variant="rectangular" width="100%" height={60} sx={{ bgcolor: "grey.300" }} />
          <Skeleton variant="rectangular" width="100%" height={350} sx={{ bgcolor: "grey.300" }} />
        </Stack>
      }
    >
      <VendorsDataGridInner facilityId={facilityId} />
    </Suspense>
  );
};
